/**
 * MobileNav — fixed bottom navigation bar for field workers (Phase B)
 *
 * Spec §04 order: Today · Tasks · Sage (center FAB, lifted, gold) · Messages · Time.
 * "Logs" removed (lives under More). Sage navigates to /m/sage (Phase C §07.4 —
 * the FAB indicator lights up when location.pathname === '/m/sage').
 *
 * Principle P-01: Today is tab 1, not Tasks.
 */
import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { tokens } from '@/design-system/tokens'
import { Home, CheckSquare, Sparkles, MessageCircle, Clock, type LucideIcon } from 'lucide-react'
import { apiGet } from '@/lib/api'
import type { ApiChatUnread, ApiActiveTimer } from '@/lib/mobile-api-types'

type RouteTab = { id: string; icon: LucideIcon; label: string; path: string }
type Tab = RouteTab

const MOBILE_TABS: readonly Tab[] = [
  { id: 'today',    icon: Home,          label: 'Today',    path: '/m/today' },
  { id: 'tasks',    icon: CheckSquare,   label: 'Tasks',    path: '/m/tasks' },
  { id: 'sage',     icon: Sparkles,      label: 'Sage',     path: '/m/sage' },
  { id: 'messages', icon: MessageCircle, label: 'Messages', path: '/m/messages' },
  { id: 'time',     icon: Clock,         label: 'Time',     path: '/m/time' },
] as const

const POLL_MS = 30_000

export function MobileNav() {
  const navigate = useNavigate()
  const location = useLocation()
  const [unreadMessages, setUnreadMessages] = useState(0)
  const [timerActive, setTimerActive] = useState(false)

  useEffect(() => {
    let cancelled = false

    function fetchUnread() {
      apiGet('/chat/unread')
        .then((res: unknown) => {
          if (!cancelled) setUnreadMessages((res as ApiChatUnread)?.total ?? 0)
        })
        .catch(() => {/* silent — badge simply won't show */})
    }

    fetchUnread()
    const timer = setInterval(fetchUnread, POLL_MS)
    return () => { cancelled = true; clearInterval(timer) }
  }, [])

  useEffect(() => {
    let cancelled = false

    function fetchTimer() {
      apiGet('/timesheets/active')
        .then((res: unknown) => {
          if (!cancelled) setTimerActive(!!(res as ApiActiveTimer)?.active)
        })
        .catch(() => {/* silent */})
    }

    fetchTimer()
    const timer = setInterval(fetchTimer, POLL_MS)
    return () => { cancelled = true; clearInterval(timer) }
  }, [])

  // Clear badge when user navigates to messages
  useEffect(() => {
    if (location.pathname === '/m/messages') setUnreadMessages(0)
  }, [location.pathname])

  return (
    <>
      {/* Bottom nav bar */}
      <nav style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: 'calc(78px + env(safe-area-inset-bottom))',
        paddingBottom: 'env(safe-area-inset-bottom)',
        background: 'var(--bg-page)',
        borderTop: '1px solid var(--border)',
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        alignItems: 'flex-start',
        padding: '10px 6px 6px',
        zIndex: 202,
      }}>
        {MOBILE_TABS.map(tab => {
          const isSage = tab.id === 'sage'
          const isMessages = tab.id === 'messages'
          const isTime = tab.id === 'time'
          const isActive = location.pathname === tab.path

          return (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              aria-label={tab.label}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: '4px',
                paddingTop: isSage ? 0 : '6px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                minHeight: 44,
              }}
            >
              {isSage ? (
                <span style={{
                  display: 'grid',
                  placeItems: 'center',
                  width: 46,
                  height: 46,
                  borderRadius: '50%',
                  background: 'var(--brand-gold)',
                  color: 'var(--brand-gold-text)',
                  marginTop: '-12px',
                  boxShadow: '0 6px 20px color-mix(in oklch, var(--brand-gold) 35%, transparent)',
                }}>
                  <tab.icon size={22} strokeWidth={2.2} />
                </span>
              ) : isMessages ? (
                <span style={{ position: 'relative', display: 'inline-flex' }}>
                  <tab.icon
                    size={22}
                    color={isActive ? 'var(--brand-gold)' : 'var(--text-muted)'}
                    strokeWidth={1.7}
                  />
                  {unreadMessages > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: -3,
                      right: -3,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'var(--st-error)',
                      border: '1.5px solid var(--bg-elevated)',
                    }} aria-label={`${unreadMessages} unread messages`} />
                  )}
                </span>
              ) : isTime ? (
                <span style={{ position: 'relative', display: 'inline-flex' }}>
                  <tab.icon
                    size={22}
                    color={isActive ? 'var(--brand-gold)' : 'var(--text-muted)'}
                    strokeWidth={1.7}
                  />
                  {timerActive && (
                    <span style={{
                      position: 'absolute',
                      top: -3,
                      right: -3,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: 'var(--st-success)',
                      border: '1.5px solid var(--bg-elevated)',
                    }} aria-label="Timer running" />
                  )}
                </span>
              ) : (
                <tab.icon
                  size={22}
                  color={isActive ? 'var(--brand-gold)' : 'var(--text-muted)'}
                  strokeWidth={1.7}
                />
              )}
              <span style={{
                fontSize: '10.5px',
                fontWeight: 600,
                letterSpacing: '0.3px',
                color: isActive
                  ? 'var(--brand-gold)'
                  : (isSage ? 'var(--text-secondary)' : 'var(--text-muted)'),
                fontFamily: tokens.typography.fontFamily.sans,
                lineHeight: 1,
              }}>
                {tab.label}
              </span>
            </button>
          )
        })}
      </nav>
    </>
  )
}
